<template>
    <e-drawer ref="eDrawer" :snap-points="['116px']" :sidebar="isDesktopDevice" :cover-height="300"
        :no-share-button="hideTopDrawerButtons" :no-close-button="hideTopDrawerButtons" @share="shareLocation"
        :title="displayName" @close="$emit('close')" @snap-position-change="updateMoreMenuPlacement">

        <template #above>
            <div v-if="previewImage" class="image-preview" @click="imageModal = true">
                <b-img :src="previewImage" fluid />
            </div>
            <div class="d-flex flex-column ml-auto mt-auto">
                <b-btn variant="white" @click="$emit('reset-camera')" class="pill-btn-xl">
                    <i :class="[fontAwesomePrefix, 'fa-crosshairs fa-lg']" />
                </b-btn>
            </div>
        </template>

        <template v-if="previewImage" #cover-image>
            <div class="cover-image" @click="imageModal = true">
                <b-img :src="previewImage" fluid />
            </div>
        </template>

        <template #header-actions>
            <div class="d-flex justify-content-center align-items-center w-100">
                <b-btn variant="accent" size="lg" class="flex-grow-1 mr-2" @click="$emit('directions')">
                    <i :class="[fontAwesomePrefix, 'fa-location-arrow']" />
                    {{ $t('map.directions') }}
                </b-btn>

                <b-btn variant="light" size="lg" @click="moreMenu = true">
                    <i :class="[fontAwesomePrefix, 'fa-ellipsis']" />
                    {{ $t('map.more') }}
                </b-btn>
            </div>
        </template>

        <e-action-sheet v-model="moreMenu" :title="displayName" :description="$t('map.choseAction')">
            <e-action-sheet-item @click="openDetails()">
                <i :class="[fontAwesomePrefix, 'fa-info-circle']" />
                {{ $t('map.showLocationInfos') }}
            </e-action-sheet-item>
            <e-action-sheet-item @click="$emit(`start-from`)">
                <i :class="[fontAwesomePrefix, 'fa-dot-circle']" />
                {{ $t('map.startFrom') }}
            </e-action-sheet-item>
            <e-action-sheet-item @click="$emit(`directions`)">
                <i :class="[fontAwesomePrefix, 'fa-location-dot']" />
                {{ $t('map.goToLocation') }}
            </e-action-sheet-item>
            <e-action-sheet-item v-if="cmsWebsite.settings.testing?.enabled" @click="$store.commit(`showFeedbackModal`, {action: `location-drawer`, cmsItem})">
                <i :class="[fontAwesomePrefix, 'fa-comment-smile', 'mr-2']" />
                <span v-tr>Give Feedback|Donner des commentaires</span>
            </e-action-sheet-item>
        </e-action-sheet>

        <map-mobile-location-info v-if="mapItem" :map-item="mapItem"/>

        <map-items-selector v-if="cmsItem && cmsItem.mapItems && cmsItem.mapItems.length > 1"
                :map-items="cmsItem.mapItems" :selected-map-item-id="mapItem.id"
                @selected="selectEndPointMapItem" class="mt-3"/>

        <b-btn variant="light" class="py-2" v-if="cmsWebsite.settings.testing?.enabled" block @click="$store.commit(`showFeedbackModal`, {action: `location-drawer`, cmsItem})">
            <i :class="[fontAwesomePrefix, 'fa-comment-smile']" />
            <span class="ml-1" v-tr>Give Feedback|Donner des commentaires</span>
        </b-btn>

        <e-mobile-modal v-model="imageModal" position="center" :title="displayName">
            <div class="w-100 ratio-1x1 rounded-lg bg-light overflow-hidden">
                <b-img :src="previewImage" :alt="displayName" block fluid />
            </div>
        </e-mobile-modal>
    </e-drawer>
</template>

<script>
import EDrawer from "@/components/e-drawer.vue";
import EMobileModal from "@/components/e-mobile-modal.vue";
import MapMobileLocationInfo from "@/components/maps/mobile/map-mobile-location-info.vue";
import EActionSheet from "@vue-components/components/e-action-sheet.vue";
import EActionSheetItem from "@vue-components/components/e-action-sheet-item.vue";
import MapItemsSelector from "@components/maps/mobile/map-items-selector.vue";

export default {
    name: `map-mobile-location-drawer`,
    components: {EActionSheetItem, EActionSheet, EDrawer, EMobileModal, MapMobileLocationInfo, MapItemsSelector },
    props: {
        mapItem: { type: Object, default: null },
        cmsItem: { type: Object, default: null },
        distance: { type: Number, default: null },
        duration: { type: Number, default: null }
    },
    data() {
        return {
            imageModal: false,
            isMoreMenuOpen: false,
            moreMenuPlacement: ``,
            moreMenu: false
        };
    },
    computed: {
        hideTopDrawerButtons() {
            return this.isMoreMenuOpen && this.moreMenuPlacement === `top-start`;
        },
        displayName() {
            if (this.mapItem?.getFullName) {
                return this.mapItem.getFullName();
            }
            return this.mapItem?.getName?.() || this.cmsItem?.getName?.() || this.$t(`map.destination`) || `Destination`;
        },
        secondaryValue() {
            return this.cmsItem?.getSecondaryValue?.() ? this.$tr(this.cmsItem.getSecondaryValue()) : null;
        },
        previewImage() {
            if (this.mapItem.image) {
                return this.mapItem?.image;
            } else if (this.mapItem.cmsItem?.getPreviewImage) {
                return this.mapItem.cmsItem.getPreviewImage();
            }
            return null;
        }
    },
    methods: {
        getFieldValueByType(type) {
            if (!this.cmsItem?.typeDef?.fields) return null;
            const field = this.cmsItem.typeDef.fields.find((f) => f.type === type);
            if (!field) return null;
            return this.cmsItem.getFieldFormatted?.(field.id) || this.cmsItem[field.id];
        },
        openDetails() {
            setTimeout(() => {
                const drawer = this.$refs.eDrawer;
                if (!drawer) return;
                drawer.sheetPosition > 200 ? drawer.snapToMin() : drawer.snapToMax();
            }, 50);
        },
        onMoreMenuShow() {
            this.isMoreMenuOpen = true;
            this.updateMoreMenuPlacement();
        },
        onMoreMenuShown() {
            this.$nextTick(() => {
                this.updateMoreMenuPlacement();
            });
        },
        onMoreMenuHide() {
            this.isMoreMenuOpen = false;
            this.moreMenuPlacement = ``;
        },
        getMoreDropdownMenu() {
            return this.$refs.moreDropdown?.$el?.querySelector(`ul.dropdown-menu`) || null;
        },
        updateMoreMenuPlacement(options = null) {
            if (options && options.snapPosition === `5`) {
                this.moreMenuPlacement = `top-start`;
                return;
            }

            const dropdownMenu = this.getMoreDropdownMenu();
            if (!dropdownMenu) {
                this.moreMenuPlacement = ``;
                return;
            }
            this.moreMenuPlacement = dropdownMenu.getAttribute(`x-placement`) || ``;
        },
        shareLocation() {
            const link = this.cmsItem?.getMapLink?.();
            if (navigator.share && link) {
                navigator.share({ title: this.displayName, url: link }).catch(() => { });
            }
        },
        selectEndPointMapItem(mapItem) {
            this.$emit(`item-selected`, mapItem);
            this.$refs.eDrawer?.snapToMin();
        },
    },
};
</script>

<style lang="scss" scoped>
.map-mobile-location {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1010;
    pointer-events: none;
}

.image-preview {
    width: 110px;
    height: 110px;
    border-radius: 14px;
    border: 2px solid #fff;
    overflow: hidden;
    background: #fff;
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.18);
    padding: 0;
    pointer-events: auto;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
    }
}

.drawer-footer {

    .start-btn {
        width: 66%;
    }
}
</style>
